import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { LiveClassService } from '../../shared/core/state/live-class/live-class.service';
import {
  AnnouncementItem,
  AnnouncementAuthor,
} from '../../shared/models/announcement.model';
import { ToastService } from '@black/ui';

@Component({
  selector: 'black-class-announcements',
  templateUrl: './class-announcements.component.html',
  styleUrl: './class-announcements.component.css',
  standalone: false,
})
export class ClassAnnouncementsComponent implements OnInit, OnChanges {
  private liveClassService = inject(LiveClassService);
  private toastService = inject(ToastService);

  @Input() liveClassId: string | undefined = '';
  @Input() isInstructor = true;

  announcements: AnnouncementItem[] = [];
  isLoading = false;
  isPosting = false;
  showModal = false;

  announcementForm = {
    title: '',
    message: '',
    pinned: false,
  };

  ngOnInit(): void {
    if (this.liveClassId && !this.announcements.length && !this.isLoading) {
      this.fetchAnnouncements();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['liveClassId']) {
      const current = changes['liveClassId'].currentValue;
      const previous = changes['liveClassId'].previousValue;
      if (current && current !== previous) {
        this.fetchAnnouncements();
      }
    }
  }

  fetchAnnouncements(): void {
    if (!this.liveClassId) return;
    this.isLoading = true;
    this.liveClassService.getAnnouncements(this.liveClassId).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res?.success && res.data) {
          this.announcements = res.data;
        }
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  openAnnouncementModal(): void {
    this.announcementForm = { title: '', message: '', pinned: false };
    this.showModal = true;
  }

  closeAnnouncementModal(): void {
    this.showModal = false;
  }

  postAnnouncement(): void {
    if (
      !this.liveClassId ||
      !this.announcementForm.title.trim() ||
      !this.announcementForm.message.trim() ||
      this.isPosting
    ) {
      return;
    }

    this.isPosting = true;
    this.liveClassService
      .createAnnouncement(this.liveClassId, this.announcementForm)
      .subscribe({
        next: (res) => {
          this.isPosting = false;
          this.showModal = false;
          if (res?.success && res.data) {
            this.announcements = [res.data, ...this.announcements];
          } else {
            this.fetchAnnouncements();
          }
          this.toastService.showToast(
            'Announcement posted and notifications sent',
            'success'
          );
        },
        error: () => {
          this.isPosting = false;
          this.toastService.showToast('Failed to post announcement', 'error');
        },
      });
  }

  deleteAnnouncement(id: string): void {
    if (!this.liveClassId || !confirm('Are you sure you want to delete this announcement?')) return;
    this.liveClassService.deleteAnnouncement(this.liveClassId, id).subscribe({
      next: (res) => {
        if (res?.success) {
          this.announcements = this.announcements.filter((a) => a._id !== id);
          this.toastService.showToast('Announcement deleted', 'info');
        }
      },
      error: () => {
        this.toastService.showToast('Failed to delete announcement', 'error');
      },
    });
  }

  getAuthorDisplayName(author: AnnouncementAuthor | undefined): string {
    if (!author) return 'Instructor';
    const first = author.profile?.personal?.firstName;
    const last = author.profile?.personal?.lastName;
    return `${first || ''} ${last || ''}`.trim() || author.name || 'Instructor';
  }
}
